import { createTheme } from '@mui/material/styles';

// Create a consistent theme configuration
export const createAppTheme = (mode = 'light') => {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: mode === 'dark' ? '#4db6ac' : '#1976d2',
        light: mode === 'dark' ? '#80cbc4' : '#42a5f5',
        dark: mode === 'dark' ? '#00897b' : '#1565c0',
      },
      secondary: {
        main: mode === 'dark' ? '#f48fb1' : '#dc004e',
        light: mode === 'dark' ? '#ffc1e3' : '#ba68c8',
        dark: mode === 'dark' ? '#bf5f82' : '#7b1fa2',
      },
      success: {
        main: '#2e7d32',
        light: '#4caf50',
        dark: '#1b5e20',
      },
      warning: {
        main: '#ed6c02',
        light: '#ff9800',
        dark: '#e65100',
      },
      error: {
        main: '#d32f2f',
        light: '#f44336',
        dark: '#c62828',
      },
      info: {
        main: '#0288d1',
        light: '#03a9f4',
        dark: '#01579b',
      },
      background: {
        default: mode === 'dark' ? '#0d0d0d' : '#fafafa',
        paper: mode === 'dark' ? '#181818' : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#f0f0f0' : '#212121',
        secondary: mode === 'dark' ? '#888888' : '#757575',
      },
      divider: mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0, 0, 0, 0.12)',
    },
        typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      // Smaller, more compact typography
      h1: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h3: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h4: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h5: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h6: {
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: 1.2,
      },
      body1: {
        fontSize: '0.875rem',
        lineHeight: 1.4,
      },
      body2: {
        fontSize: '0.75rem',
        lineHeight: 1.4,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.8125rem',
      },
    },
        shape: {
      borderRadius: 6,
    },
    spacing: (factor) => {
      // Dynamic spacing based on compact mode
      const baseSpacing = 8;
      const compactSpacing = 4;
      const isCompact = document.body.classList.contains('compact-compact');
      return `${(isCompact ? compactSpacing : baseSpacing) * factor}px`;
    },
    components: {
      // Theme-aware scrollbars
      MuiCssBaseline: {
        styleOverrides: {
          '& ::-webkit-scrollbar-track': {
            background: mode === 'dark' ? '#1a1a1a' : '#f1f1f1',
          },
          '& ::-webkit-scrollbar-thumb': {
            background: mode === 'dark' ? '#444444' : '#c1c1c1',
            borderRadius: '4px',
          },
          '& ::-webkit-scrollbar-thumb:hover': {
            background: mode === 'dark' ? '#555555' : '#a8a8a8',
          },
        },
      },
      // Global button overrides - more compact
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            textTransform: 'none',
            fontWeight: 500,
            padding: '6px 16px', // Reduced padding
            minHeight: '32px', // Smaller minimum height
            fontSize: '0.8125rem',
          },
          small: {
            padding: '4px 12px',
            minHeight: '28px',
            fontSize: '0.75rem',
          },
          large: {
            padding: '8px 20px',
            minHeight: '40px',
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
            },
          },
        },
      },
                  // Global form control overrides - more compact
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontSize: '0.875rem',
            minHeight: '40px', // Ensure minimum height for proper alignment
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? '#4db6ac' : '#1976d2',
            },
          },
          input: {
            padding: '8px 12px', // Reduced padding
            height: 'auto',
            display: 'flex',
            alignItems: 'center',
          },
        },
      },
      // Compact form labels — always shrunk so value="" Selects never overlap
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            lineHeight: 1,
          },
          outlined: {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      },
      // Compact select
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: '8px 12px',
            minHeight: 'auto',
            display: 'flex',
            alignItems: 'center',
          },
        },
      },
            // Global chip overrides - more compact
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
            height: '24px', // Smaller chips
            fontSize: '0.75rem',
          },
          small: {
            height: '20px',
            fontSize: '0.6875rem',
          },
        },
      },
            // Enhanced table header styling - more compact
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: mode === 'dark' ? '#1f1f1f' : '#e8e8e8',
              color: mode === 'dark' ? '#f0f0f0' : '#000000',
              fontWeight: 700,
              fontSize: '0.75rem',
              padding: '8px 12px',
              borderBottom: mode === 'dark' ? '2px solid rgba(255,255,255,0.10)' : '2px solid #bdbdbd',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            },
          },
        },
      },
      // Enhanced table cell styling - more compact
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: mode === 'dark' ? `1px solid rgba(255,255,255,0.08)` : '1px solid #e0e0e0',
            color: mode === 'dark' ? 'inherit' : '#212121',
            padding: '6px 12px',
            fontSize: '0.8125rem',
          },
          head: {
            backgroundColor: mode === 'dark' ? '#1f1f1f !important' : '#e8e8e8 !important',
            color: mode === 'dark' ? '#f0f0f0 !important' : '#000000 !important',
            fontWeight: '700 !important',
            textTransform: 'uppercase !important',
            letterSpacing: '0.5px !important',
            padding: '8px 12px !important',
            fontSize: '0.75rem !important',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05) !important' : undefined,
            },
            '&:nth-of-type(even)': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : undefined,
            },
          },
        },
      },
      // Enhanced paper styling for better contrast
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: mode === 'dark' ? '#181818' : '#ffffff',
            color: mode === 'dark' ? '#f0f0f0' : '#212121',
          },
        },
      },
      // Global card overrides - more compact
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: mode === 'dark' ? '#181818' : '#ffffff',
            color: mode === 'dark' ? '#f0f0f0' : '#212121',
          },
        },
      },
      // Compact card content
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '12px', // Reduced padding
            '&:last-child': {
              paddingBottom: '12px',
            },
          },
        },
      },
            // Global dialog overrides - more compact
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
          },
        },
      },
      // Compact dialog content
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '16px', // Reduced padding
          },
        },
      },
      // Compact dialog actions
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '12px 16px', // Reduced padding
          },
        },
      },
      // Compact toolbar
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '48px !important', // Reduced height
            paddingLeft: '16px !important',
            paddingRight: '16px !important',
          },
        },
      },
      // Compact app bar
      MuiAppBar: {
        styleOverrides: {
          root: {
            minHeight: '48px',
          },
        },
      },
      // Compact list items
      MuiListItem: {
        styleOverrides: {
          root: {
            paddingTop: '4px',
            paddingBottom: '4px',
          },
        },
      },
      // Compact list item text
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontSize: '0.875rem',
          },
          secondary: {
            fontSize: '0.75rem',
          },
        },
      },
    },
  });
};

export default createAppTheme;
